import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, darken } from '@material-ui/core/styles';
import {
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core';

import { Network, SocialAccount, useDeleteSocialConnectionMutation } from 'api';
import { Alert, SocialIcon } from 'components';
import { connectionPopup, getSocialAccountName } from 'helpers';
import { useDeleteSocialConnection } from 'updaters';
import { useNetworkCapabilities } from 'hooks';

interface IProps {
  account: SocialAccount;
}

const useStyles = makeStyles(theme => ({
  divider: {
    height: 1,
    margin: `${theme.spacing(2)}px 0`,
    backgroundColor: theme.palette.divider,
  },
  refreshButton: {
    marginRight: theme.spacing(),
    color: theme.palette.error.main,
    '&:hover': {
      color: darken(theme.palette.error.main, 0.1),
      backgroundColor: 'transparent',
    },
  },
}));

const Connections: FC<IProps> = ({ account }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const { hasTwitter } = useNetworkCapabilities();
  const mutationOptions = useDeleteSocialConnection();
  const { mutate } = useDeleteSocialConnectionMutation(mutationOptions);

  function handleDisconnect() {
    setDeleteOpen(false);
    mutate({ id: account.id });
  }

  function renderDeleteAlert() {
    return (
      <Alert
        open={deleteOpen}
        onConfirm={handleDisconnect}
        onCancel={() => setDeleteOpen(false)}
        message={t('user:disconnectAlert')}
        confirm={t('common:delete')}
      />
    );
  }

  function renderDisconnect() {
    return (
      <Button variant="outlined" onClick={() => setDeleteOpen(true)}>
        {t('common:disconnect')}
      </Button>
    );
  }

  function renderRefresh() {
    return (
      <Button
        variant="outlined"
        className={classes.refreshButton}
        onClick={() => connectionPopup(account.provider)}
      >
        {t('common:refresh')}
      </Button>
    );
  }

  if (account.provider === Network.Twitter && !hasTwitter) {
    return null;
  }

  return (
    <Fragment>
      {renderDeleteAlert()}
      <ListItem disableGutters key={account.id}>
        <ListItemIcon>
          <SocialIcon provider={account.provider} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2' }}
          primary={getSocialAccountName(account)}
        />
        <ListItemSecondaryAction>
          {account.expires_soon && renderRefresh()}
          {renderDisconnect()}
        </ListItemSecondaryAction>
      </ListItem>
      <li className={classes.divider} />
    </Fragment>
  );
};

export default Connections;
